import React, { Dispatch, SetStateAction } from "react";
import { QueryObserverResult } from "react-query";
import { Link } from "react-router-dom";

import { Organization, BaseOrganization } from "@secondcloset/types/src/_common";

import { Checkbox, Space, Table, Tag } from "antd";
import { get, startCase } from "lodash-es";
import moment from "moment";

import TablePagination from "components/_common/TablePagination";
import { formatNumber } from "helperFunctions";
import { orgStatusColor } from "../../../../../lib/organization";

interface OrganizationsTableProps {
  isSavingCSVReport: boolean;
  isCsvReportGeneratorVisible: boolean;
  areAllItemsSelected: boolean;
  organizationIndex: QueryObserverResult<Organization[], unknown>;
  selectedItems: [React.Key, string][];
  setSelectedItems: Dispatch<SetStateAction<[React.Key, string][]>>;
  excludedItems: React.Key[];
  setExcludedItems: Dispatch<SetStateAction<React.Key[]>>;
  PAGE_SIZE: number;
  setAreAllItemsSelected: Dispatch<SetStateAction<boolean>>;
  newResult: Array<[React.Key, string]>;
  urlState: Partial<{
    search: string;
    page: number;
  }>;
  setUrlState: Dispatch<
    SetStateAction<
      Partial<{
        search: string;
        page: number;
      }>
    >
  >;
}

export const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
  isSavingCSVReport,
  organizationIndex,
  isCsvReportGeneratorVisible,
  areAllItemsSelected,
  setSelectedItems,
  setAreAllItemsSelected,
  newResult,
  selectedItems,
  excludedItems,
  urlState,
  setUrlState,
  setExcludedItems,
  PAGE_SIZE,
}) => {
  const buildColumns = () => {
    const renderOrganizationLink = (_: string, record: BaseOrganization) => {
      const path = `/organizations/${get(record, "id")}`;
      if (isCsvReportGeneratorVisible) {
        return get(record, "name");
      } else {
        return <Link to={path}>{get(record, "name")}</Link>;
      }
    };

    const renderDateCreated = (date: moment.MomentInput) => {
      return moment(date).format("ll");
    };

    const renderPhoneNumber = (phoneNumber = "-") => {
      return formatNumber(phoneNumber);
    };

    const columns = [
      {
        title: "Organization Name",
        dataIndex: "name",
        key: "name",
        render: renderOrganizationLink,
        ellipsis: true,
        width: 250,
      },
      {
        title: "Phone #",
        dataIndex: "phone_number",
        key: "phone_number",
        render: renderPhoneNumber,
      },
      {
        title: "Date Created",
        dataIndex: "created_at",
        key: "created_at",
        render: renderDateCreated,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: BaseOrganization["status"] = "active") => (
          <Tag color={orgStatusColor[status]}>{startCase(status)}</Tag>
        ),
      },
    ];

    return columns;
  };

  const headerCheckbox = (
    <Space>
      <Checkbox
        checked={areAllItemsSelected}
        onChange={() => {
          if (!areAllItemsSelected) {
            setSelectedItems([...newResult]);
          }
          setAreAllItemsSelected(!areAllItemsSelected);
        }}
      />
      All
    </Space>
  );
  return (
    <div className="organizations-table">
      <Table
        loading={organizationIndex.isLoading || organizationIndex.isPreviousData || isSavingCSVReport}
        columns={buildColumns()}
        dataSource={organizationIndex.data || []}
        size="small"
        pagination={false}
        scroll={{ x: 800 }}
        rowKey={(organization) => get(organization, "id")}
        rowSelection={{
          columnTitle: isCsvReportGeneratorVisible ? headerCheckbox : "",
          columnWidth: isCsvReportGeneratorVisible ? "50px" : "0px",
          hideSelectAll: !isCsvReportGeneratorVisible,
          selectedRowKeys: selectedItems.filter((item) => !excludedItems.includes(item[0])).map((si) => si[0]),
          onSelect: (record, selected) => {
            if (selected) {
              setSelectedItems([...selectedItems, [record.id, record.name]]);
            } else {
              const filteredSelectedItems = selectedItems.filter((si) => si[0] !== record.id);
              setSelectedItems(filteredSelectedItems);
            }

            if (areAllItemsSelected) {
              if (!selected) {
                setExcludedItems([...excludedItems, record.id]);
              } else {
                const filteredExcludedItems = excludedItems.filter((si) => si !== record.id);
                setExcludedItems(filteredExcludedItems);
              }
            }
          },
          renderCell: (value, record, index, originNode) => {
            if (!isCsvReportGeneratorVisible) {
              return false;
            }
            return originNode;
          },
        }}
      />
      <TablePagination
        data={organizationIndex.data || []}
        page={urlState.page || 1}
        setPage={(page) => setUrlState((state) => ({ ...state, page }))}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
};
